"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import type { Editor, Range } from "@tiptap/core";
import { setHtmlUploadModalState, setOpenHtmlUploadModal } from "@/lib/html-upload-modal-helper";

interface HtmlUploadModalContextValue {
  openHtmlUploadModal: (editor: Editor, range: Range, position?: { top: number; left: number }, trigger?: HTMLElement | null) => void;
  closeHtmlUploadModal: () => void;
  isOpen: boolean;
  editor: Editor | null;
  range: Range | null;
  position: { top: number; left: number } | null;
  trigger: HTMLElement | null;
}

const HtmlUploadModalContext = createContext<HtmlUploadModalContextValue | undefined>(undefined);

export function HtmlUploadModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [range, setRange] = useState<Range | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [trigger, setTrigger] = useState<HTMLElement | null>(null);

  const openHtmlUploadModal = useCallback(
    (editorInstance: Editor, rangeInstance: Range, pos?: { top: number; left: number }, triggerEl?: HTMLElement | null) => {
      setEditor(editorInstance);
      setRange(rangeInstance);
      setPosition(pos || null);
      setTrigger(triggerEl ?? null);
      setIsOpen(true);
    },
    [],
  );

  const closeHtmlUploadModal = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setEditor(null);
      setRange(null);
      setPosition(null);
      setTrigger(null);
    }, 100);
  }, []);

  useEffect(() => {
    setHtmlUploadModalState({ isOpen, from: range?.from ?? null, close: closeHtmlUploadModal });
  }, [isOpen, range, closeHtmlUploadModal]);

  useEffect(() => {
    setOpenHtmlUploadModal(openHtmlUploadModal);
    return () => {
      setOpenHtmlUploadModal(null);
    };
  }, [openHtmlUploadModal]);

  return (
    <HtmlUploadModalContext.Provider
      value={{ openHtmlUploadModal, closeHtmlUploadModal, isOpen, editor, range, position, trigger }}
    >
      {children}
    </HtmlUploadModalContext.Provider>
  );
}

export function useHtmlUploadModalContext() {
  const context = useContext(HtmlUploadModalContext);
  if (context === undefined) {
    throw new Error("useHtmlUploadModalContext must be used within a HtmlUploadModalProvider");
  }
  return context;
}
